/**
 * Routing Service - Calculate routes and travel times
 *
 * Primary:  OSRM public demo server (100% free, no API key, road-following routes)
 *           https://router.project-osrm.org
 * Fallback: straight-line distance + estimated travel time (when OSRM is unavailable)
 *
 * OSRM returns GeoJSON LineString geometry that we render in EnhancedLocationMap
 * as a road-following Polyline (instead of a straight line).
 */

import { calculateDistance } from './geocodingService';

export interface RouteResult {
  distance: number;  // in km
  duration: number;  // in minutes
  /** GeoJSON coordinates [lng, lat][] for road-following polyline rendering */
  geometry?: Array<[number, number]>;
  /** Turn-by-turn navigation steps */
  steps?: NavigationStep[];
}

export interface NavigationStep {
  /** Distance to next maneuver in meters */
  distance: number;
  /** Duration to next maneuver in seconds */
  duration: number;
  /** Street name */
  name: string;
  /** Maneuver type: turn, depart, arrive, merge, etc. */
  maneuverType: string;
  /** Maneuver modifier: left, right, straight, slight left, etc. */
  maneuverModifier?: string;
  /** Start location of this step [lat, lng] */
  location: [number, number];
  /** Human-readable instruction in Dutch */
  instruction: string;
}

/** Convert OSRM maneuver to Dutch instruction */
function buildInstruction(type: string, modifier?: string, name?: string): string {
  const street = name && name !== '' ? ` op ${name}` : '';
  switch (type) {
    case 'depart':    return `Vertrek${street}`;
    case 'arrive':    return `Je bent aangekomen${street ? ' ' + street : ''}`;
    case 'turn': {
      if (modifier === 'left')          return `Sla linksaf${street}`;
      if (modifier === 'right')         return `Sla rechtsaf${street}`;
      if (modifier === 'slight left')   return `Houd links aan${street}`;
      if (modifier === 'slight right')  return `Houd rechts aan${street}`;
      if (modifier === 'sharp left')    return `Scherp linksaf${street}`;
      if (modifier === 'sharp right')   return `Scherp rechtsaf${street}`;
      if (modifier === 'uturn')         return `Maak een U-bocht`;
      return `Ga rechtdoor${street}`;
    }
    case 'new name':    return `Ga verder${street}`;
    case 'continue':    return `Ga rechtdoor${street}`;
    case 'merge':       return `Voeg in${street}`;
    case 'on ramp':     return `Neem de oprit${street}`;
    case 'off ramp':    return `Neem de afrit${street}`;
    case 'fork': {
      if (modifier?.includes('left'))  return `Houd links aan op de splitsing${street}`;
      if (modifier?.includes('right')) return `Houd rechts aan op de splitsing${street}`;
      return `Houd rechts aan op de splitsing${street}`;
    }
    case 'roundabout':
    case 'rotary':      return `Neem de rotonde${street}`;
    default:            return `Ga rechtdoor${street}`;
  }
}

/** Fetch a driving route from OSRM and return distance, duration, road geometry and steps */
async function fetchOSRMRoute(
  fromLat: number, fromLng: number,
  toLat: number, toLng: number
): Promise<RouteResult | null> {
  try {
    const url =
      `https://router.project-osrm.org/route/v1/driving/` +
      `${fromLng},${fromLat};${toLng},${toLat}` +
      `?overview=full&geometries=geojson&steps=true`;

    const resp = await fetch(url, { signal: AbortSignal.timeout(8000) });
    if (!resp.ok) return null;

    const data = await resp.json();
    if (data.code !== 'Ok' || !data.routes?.[0]) return null;

    const route = data.routes[0];

    // Parse steps from all legs
    const steps: NavigationStep[] = [];
    for (const leg of route.legs ?? []) {
      for (const step of leg.steps ?? []) {
        const m = step.maneuver;
        steps.push({
          distance: step.distance,
          duration: step.duration,
          name: step.name || '',
          maneuverType: m.type,
          maneuverModifier: m.modifier,
          location: [m.location[1], m.location[0]], // OSRM [lng,lat] → [lat,lng]
          instruction: buildInstruction(m.type, m.modifier, step.name),
        });
      }
    }

    return {
      distance: route.distance / 1000,
      duration: Math.round(route.duration / 60),
      geometry: route.geometry?.coordinates as Array<[number, number]>,
      steps,
    };
  } catch {
    return null;
  }
}

/**
 * Calculate estimated travel time based on distance
 * Uses average speeds:
 * - City: 30 km/h
 * - Highway: 80 km/h
 * - Mixed: 50 km/h (default)
 */
function estimateTravelTime(distanceKm: number, averageSpeedKmh: number = 50): number {
  return Math.round((distanceKm / averageSpeedKmh) * 60); // Convert to minutes
}

/**
 * Calculate route between two points.
 * Tries OSRM (real road-following route) first, falls back to straight-line estimation.
 */
export async function calculateRoute(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): Promise<RouteResult> {
  // Try OSRM real-road route
  const osrm = await fetchOSRMRoute(lat1, lng1, lat2, lng2);
  if (osrm) return osrm;

  // Fallback: straight-line + speed estimate
  const distance = calculateDistance(lat1, lng1, lat2, lng2);
  let averageSpeed = 50;
  if (distance < 10) averageSpeed = 30;
  else if (distance > 50) averageSpeed = 80;

  return { distance, duration: estimateTravelTime(distance, averageSpeed) };
}

/**
 * Calculate routes from multiple origins to one destination
 * Returns array of routes sorted by total distance
 */
export async function calculateRoutesToDestination(
  origins: Array<{ lat: number; lng: number; userId: string }>,
  destination: { lat: number; lng: number }
): Promise<Array<RouteResult & { userId: string }>> {
  const routes = await Promise.all(
    origins.map(async (origin) => {
      const route = await calculateRoute(
        origin.lat,
        origin.lng,
        destination.lat,
        destination.lng
      );
      return {
        ...route,
        userId: origin.userId
      };
    })
  );
  
  return routes.sort((a, b) => a.distance - b.distance);
}

/**
 * Calculate best match location from multiple proposals
 * Returns top 3 matches based on total distance (user1 + user2)
 */
export interface BestMatch {
  locationId: string;
  location: {
    lat: number;
    lng: number;
    address: string;
    name?: string;
  };
  user1Distance: number;
  user2Distance: number;
  user1TravelTime: number;
  user2TravelTime: number;
  totalDistance: number;
  matchQuality: 'best' | 'good' | 'not_efficient';
}

export async function calculateBestMatches(
  proposals: Array<{
    id: string;
    lat: number;
    lng: number;
    address: string;
    name?: string;
  }>,
  user1Location: { lat: number; lng: number },
  user2Location: { lat: number; lng: number }
): Promise<BestMatch[]> {
  const matches = await Promise.all(
    proposals.map(async (proposal) => {
      const [user1Route, user2Route] = await Promise.all([
        calculateRoute(user1Location.lat, user1Location.lng, proposal.lat, proposal.lng),
        calculateRoute(user2Location.lat, user2Location.lng, proposal.lat, proposal.lng)
      ]);
      
      const totalDistance = user1Route.distance + user2Route.distance;
      
      // Determine match quality
      let matchQuality: 'best' | 'good' | 'not_efficient' = 'good';
      if (totalDistance < 30) {
        matchQuality = 'best'; // Total < 30km is excellent
      } else if (totalDistance > 80) {
        matchQuality = 'not_efficient'; // Total > 80km is not efficient
      }
      
      return {
        locationId: proposal.id,
        location: {
          lat: proposal.lat,
          lng: proposal.lng,
          address: proposal.address,
          name: proposal.name
        },
        user1Distance: user1Route.distance,
        user2Distance: user2Route.distance,
        user1TravelTime: user1Route.duration,
        user2TravelTime: user2Route.duration,
        totalDistance,
        matchQuality
      };
    })
  );
  
  // Sort by total distance and return top 3
  return matches
    .sort((a, b) => a.totalDistance - b.totalDistance)
    .slice(0, 3);
}
